/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useContext } from 'react';
import { isEmpty } from 'lodash';

import { Vehicle } from '@sm360/widget-commons-vehicle';
import ConditionReport from '@sm360/condition-report';
import { LocaleService } from '@sm360/widget-commons-core-services';
import { Button, Card, CardSection } from '@sm360/ui-library';

import ShowroomCBBService from 'trade-in/services/ShowroomCBBService/ShowroomCBBService.js';
import 'trade-in/components/InstantEstimateWrapper/Steps/VehicleCondition/VehicleCondition.scss';
import ActionButton from '../../Components/ActionButton/ActionButton.jsx';
import ERROR_ANSWERS from '../../../../constants/inspectionNeededAnswers.js';
import NewTradeInContext from '../../../../NewTradeInContext.js';

const { DeskingConditionForm, Models } = ConditionReport;

/*
 *  @param [rawModel] array of questions sent by backend service
 *  @param [answers]  array of object of answers {questionId: answerCode}
*/
const updateRawModelWithAnswer = (rawModel, answers) => {
    rawModel.forEach((question) => {
        // If an answer exists for this question
        const potentialAnswer = answers?.[question.questionId];
        if (typeof potentialAnswer !== 'undefined') {
            question.answerCode = potentialAnswer;
        }

        if (question?.childQuestions) {
            updateRawModelWithAnswer(question.childQuestions, answers);
        }
    });
    return rawModel;
};

const getRawAnswers = (answers) => {
    const tempAnswers = Object.assign({}, answers);
    Object.entries(tempAnswers).forEach(([questionId, answer]) => {
        let rawAnswer = answer;
        // TO DO: retrieve answerCode directly from rawModel instead of converting booleans to string
        if (typeof answer === 'boolean') {
            if (rawAnswer) rawAnswer = 'Y';
            else rawAnswer = 'N';
        }
        tempAnswers[questionId] = rawAnswer;
    });
    return tempAnswers;
};

const VehicleCondition = ({
    parentModel,
    parentRawModel,
    updateParentModel,
    tradeInVehicle,
    onPreviousStep,
    onNextStep,
    onTradeInVehicleUpdated,
    onFormSubmit,
    onError,
    questionErrorId,
}) => {
    const { useEffect, useState } = React;
    const [isQuestionsFetching, setQuestionsFetching] = useState(false);
    const [fetching, setFetching] = useState(false);
    const [error, setError] = useState(false);
    const [retry, setRetry] = useState(false);
    const [model, setModel] = useState(parentModel || {});
    const [rawModel, setRawModel] = useState(parentRawModel || {});
    const [selectedQuestions, setSelectedQuestions] = useState({});
    const [errorQuestionId, setErrorQuestionId] = useState(questionErrorId);
    const [tokenAPIError, setTokenAPIError] = useState(false);
    const { fieldsLocked } = useContext(NewTradeInContext);

    const getTradeInQuestions = (tradeVehicle) => {
        const tradeVehicleToken = {
            tradeVehicleToken: tradeVehicle.tradeVehicleToken,
        };

        setQuestionsFetching(true);
        ShowroomCBBService.getVehicleConditionQuestions(tradeVehicleToken)
            .then((results) => {
                // Saving the raw questions model to be able to send it back to /token endpoint
                setRawModel(results);
                const formattedModel = Models.DilawriCBBModelGenerator(results);
                updateParentModel(formattedModel, results);
                setModel(formattedModel);
                return results;
            })
            .catch((e) => {
                console.error(
                    'CBBAutoComplete#loadQuestions error calling ShowroomCBBService.getVehicleConditionQuestions',
                    e,
                    tradeVehicle.token
                );
                setError(true);
            })
            .finally(() => {
                setQuestionsFetching(false);
            });
    };

    const sendVehicleQuestionsForm = ({ conditionFormList }) => {
        const selectedOptions = tradeInVehicle?.selectedVehicleOptions
            ? tradeInVehicle?.selectedVehicleOptions.map((option) => option.code)
            : null;

        const rawAnswers = getRawAnswers(tradeInVehicle?.conditions);
        const rawQuestionsAnswers = updateRawModelWithAnswer(rawModel, rawAnswers);

        if (fieldsLocked) return onNextStep();

        setFetching(true);
        ShowroomCBBService.updateTradeVehicleToken(
            tradeInVehicle.tradeVehicleToken,
            tradeInVehicle?.odometer,
            tradeInVehicle?.exteriorColor?.description,
            selectedOptions,
            rawQuestionsAnswers
        )
            .then((result) => {
                tradeInVehicle.tradeVehicleToken = result.tradeVehicleToken;
                // Saving a formatted version of condition report for email sending
                tradeInVehicle.formattedConditionReport = conditionFormList;
                return onFormSubmit(tradeInVehicle);
            })
            .catch(() => {
                setTokenAPIError(true);
            })
            .finally(() => {
                setFetching(false);
            });
        return false;
    };

    // Fetch questions whenever a refresh is asked
    useEffect(
        () => {
            if (isEmpty(model)) getTradeInQuestions(tradeInVehicle);
        },
        [retry]
    );

    // Fetch the saved user selection whenever the model changes
    useEffect(
        () => {
            if (model && !isEmpty(tradeInVehicle.conditions)) {
                setSelectedQuestions(tradeInVehicle.conditions);
            }
        },
        [model]
    );

    // Scroll to error question if exists
    // Wait for 200ms to let the open/collapse animation finish
    useEffect(
        () => {
            setTimeout(() => {
                if (errorQuestionId) {
                    const errorQuestionHtml = document.getElementById(`question-${errorQuestionId}`);
                    if (errorQuestionHtml) {
                        errorQuestionHtml.scrollIntoView({ block: 'center', behavior: 'smooth' });
                    }
                }
            }, 200);
        },
        [errorQuestionId]
    );

    // Watch for user question answers and redirect to Inspection needed if applicable
    const handleChange = (questionId, questionOptions, answerId, answerOptions) => {
        tradeInVehicle.conditions = { ...tradeInVehicle.conditions, ...{ [questionId]: answerId } };

        if (ERROR_ANSWERS[questionId] === answerId) {
            const rawAnswers = getRawAnswers(tradeInVehicle?.conditions);
            const rawQuestionsAnswers = updateRawModelWithAnswer(rawModel, rawAnswers);
            return onError(questionOptions?.labelKey, answerOptions?.labelKey, questionId, rawQuestionsAnswers);
        }
        setErrorQuestionId(undefined);
        return onTradeInVehicleUpdated(tradeInVehicle);
    };

    const actionsButtons = (onSave) => (
        <section className="clearfix vehicle-buttons-container" data-test="trade-in.vehicle-form.submit">
            <div className="vehicle-buttons">
                <ActionButton onClick={() => onPreviousStep()}>
                    {LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_OPTIONS')}
                </ActionButton>
                <ActionButton next onClick={onSave} loading={fetching}>
                    {LocaleService.t('TRADE_IN.SECTIONS.CONTACT_INFO')}
                </ActionButton>
            </div>
        </section>
    );

    return (
        <div className="vehicle-condition-form">
            {tokenAPIError && (
                // Display an error box if CBB API returns an error on valuation token generation
                <div className="API-error-msg">
                    <Card color="alert" outline>
                        <CardSection>
                            <icon className="icon-Warning" />
                            <p>{LocaleService.t('TRADE_IN.ERROR')}</p>
                        </CardSection>
                    </Card>
                </div>
            )}
            <h5>{LocaleService.t('TRADE_IN.SECTIONS.VEHICLE_CONDITION')}</h5>
            <p className="sr-text">{LocaleService.t('TRADE_IN.VEHICLE_CONDITION.SUBTITLE')}</p>
            {isQuestionsFetching ? (
                <div className="loading" />
            ) : error ? (
                <div className="error">
                    <p>{LocaleService.t('TRADE_IN.SECTIONS_CONTENT.CONTACT_INFO.ERROR')}</p>
                    <Button onClick={() => setRetry(!retry)} className="retry-button">
                        {LocaleService.t('TRADE_IN.BUTTONS.RETRY')}
                    </Button>
                    {actionsButtons(sendVehicleQuestionsForm)}
                </div>
            ) : (
                <DeskingConditionForm
                    model={model}
                    initialAnswers={selectedQuestions}
                    validationFields="allRequired"
                    lang={LocaleService.getSimpleLocale()}
                    onSave={sendVehicleQuestionsForm}
                    onChange={handleChange}
                    customClassForQuestionId={errorQuestionId}
                    helperIconHTML={<icon className="icon-info" />}
                    alwaysExpanded
                    noHeader
                    noSectionHeader
                    rawInputs
                    readOnly={fieldsLocked}
                >
                    {({ handleSaveTradeIn }) => actionsButtons(handleSaveTradeIn)}
                </DeskingConditionForm>
            )}
        </div>
    );
};

VehicleCondition.propTypes = {
    tradeInVehicle: PropTypes.instanceOf(Vehicle.TradeInVehicleModel).isRequired,
    onPreviousStep: PropTypes.func.isRequired,
    onTradeInVehicleUpdated: PropTypes.func.isRequired,
    onFormSubmit: PropTypes.func.isRequired,
};

VehicleCondition.displayName = 'VehicleCondition';

export default VehicleCondition;
